/**
 * Settings → Integrations → Calendly — V5-03 §2.
 *
 * The deliveries list is the point of this screen. Webhook setup fails quietly
 * by nature: you paste a key, nothing happens, and there is nothing to look at.
 * Showing the last twenty attempts — including the rejected ones and why —
 * turns "it doesn't work" into "the signing key has a trailing space".
 */

import { useState } from 'react';
import { CalendarCheck, Check, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { writeOnlySecretValue } from '@/features/platform/savePayload';
import { ApiError } from '@/lib/api';
import {
  useCalendlyDeliveries,
  useCalendlySettings,
  useSaveCalendly,
} from '@/features/calendar/api';

/** Plain English for each outcome — the enum values are for the wire. */
const OUTCOME_LABEL: Record<string, { text: string; tone: 'ok' | 'warn' | 'bad' }> = {
  matched: { text: 'Booking recorded', tone: 'ok' },
  ignored_no_member: { text: 'Ignored — host is not a member here', tone: 'warn' },
  ignored_duplicate: { text: 'Ignored — already recorded', tone: 'warn' },
  ignored_unhandled_event: { text: 'Ignored — event type we do not use', tone: 'warn' },
  rejected_bad_signature: { text: 'Rejected — signature did not verify', tone: 'bad' },
  rejected_malformed: { text: 'Rejected — payload could not be read', tone: 'bad' },
};

export function CalendlySection() {
  const { data, isLoading } = useCalendlySettings();
  const { data: deliveries = [] } = useCalendlyDeliveries();
  const save = useSaveCalendly();
  const [key, setKey] = useState('');
  const [copied, setCopied] = useState(false);

  async function copyUrl() {
    if (!data?.webhookUrl) return;
    try {
      await navigator.clipboard.writeText(data.webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copied');
    } catch {
      toast.error('Could not copy — select the text and copy it manually');
    }
  }

  async function submit() {
    try {
      /**
       * V10 §11 — the shared write-only rule, not a raw string.
       *
       * An empty box used to send `''`, which the API read as "clear this" —
       * so pressing Save with nothing typed silently deleted a working signing
       * key and every Calendly webhook started failing its signature check.
       */
      const value = writeOnlySecretValue(key, Boolean(data?.signingKey.isSet));
      if (value === undefined) {
        toast.message('Nothing to save — paste a key first');
        return;
      }
      await save.mutateAsync({ signingKey: value });
      setKey('');
      toast.success('Calendly signing key saved');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save the signing key');
    }
  }

  if (isLoading) return null;

  return (
    <section className="vt-surface p-4" data-testid="calendly-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-[14px] font-medium">
            <CalendarCheck className="size-4" />
            Calendly
          </h2>
          <p className="mt-1 max-w-lg text-[12px] vt-muted">
            Bookings made through Calendly appear on the calendar of whichever member hosted them.
            Bookings are calendar entries only — they never create tasks.
          </p>
        </div>
        <Badge tone={data?.signingKey.isSet ? 'accent' : 'neutral'}>
          {data?.signingKey.isSet ? 'Connected' : 'Not configured'}
        </Badge>
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <Label>Webhook URL</Label>
          <div className="mt-1 flex gap-2">
            <Input
              readOnly
              value={data?.webhookUrl ?? ''}
              data-testid="calendly-webhook-url"
              onFocus={(e) => e.currentTarget.select()}
              className="vt-mono text-[12px]"
            />
            <Button variant="secondary" onClick={copyUrl} data-testid="calendly-copy-url">
              {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            </Button>
          </div>
        </div>

        <div>
          <Label htmlFor="calendly-key">Signing key</Label>
          <div className="mt-1 flex gap-2">
            <Input
              id="calendly-key"
              type="password"
              autoComplete="off"
              placeholder={data?.signingKey.masked ?? 'Paste the key Calendly showed you'}
              value={key}
              onChange={(e) => setKey(e.target.value)}
              data-testid="calendly-signing-key"
            />
            <Button onClick={submit} disabled={!key || save.isPending} data-testid="calendly-save">
              {save.isPending ? <Loader2 className="size-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      <details className="mt-4 text-[12px]">
        <summary className="cursor-pointer vt-muted">How to set this up in Calendly</summary>
        <ol className="mt-2 list-decimal space-y-1 pl-4 vt-muted">
          <li>Sign in to Calendly as an account admin and open Integrations → API &amp; webhooks.</li>
          <li>Under “Webhook subscriptions”, click “Create new webhook”.</li>
          <li>Paste the webhook URL above into the “URL” field.</li>
          <li>
            Tick the events <strong>invitee.created</strong> and{' '}
            <strong>invitee.canceled</strong>. Leave the rest off — we ignore them.
          </li>
          <li>Choose the scope (organisation, so every member’s bookings arrive) and create it.</li>
          <li>
            Calendly then shows a <strong>signing key</strong> once. Copy it into the field above
            and save.
          </li>
        </ol>
      </details>

      <div className="mt-4">
        <h3 className="text-[13px] font-medium">Recent webhook deliveries</h3>
        {deliveries.length === 0 ? (
          <p className="mt-1 text-[12px] vt-muted" data-testid="calendly-deliveries-empty">
            Nothing received yet. Once the webhook is saved in Calendly, the next booking shows up
            here — including if it is rejected.
          </p>
        ) : (
          <ul className="mt-2 space-y-1" data-testid="calendly-deliveries">
            {deliveries.map((row) => {
              const label = OUTCOME_LABEL[row.outcome] ?? { text: row.outcome, tone: 'warn' as const };
              return (
                <li
                  key={row._id}
                  className="flex items-baseline justify-between gap-3 border-b border-[var(--border)] pb-1 text-[12px] last:border-b-0"
                  data-testid="calendly-delivery-row"
                >
                  <span
                    className={
                      label.tone === 'ok'
                        ? 'text-[var(--ok)]'
                        : label.tone === 'bad'
                          ? 'text-[var(--danger)]'
                          : 'vt-muted'
                    }
                  >
                    {label.text}
                    {row.detail ? <span className="vt-muted"> — {row.detail}</span> : null}
                  </span>
                  <span className="vt-mono shrink-0 vt-muted">
                    {new Date(row.receivedAt).toLocaleString('en-GB')}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
